import React, { useState, useEffect, useRef } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { Menu, X, ChevronDown, Phone, Mail } from 'lucide-react'
import { getOptimizedImageUrl } from '../lib/imageOptimization.js'

export default function Header() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1200)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [settings, setSettings] = useState(null)
  const dropdownRef = useRef(null)
  const location = useLocation()

  useEffect(() => {
    let isMounted = true
    const loadSettings = async () => {
      try {
        const res = await fetch('/_api/public/content/generelle_indstillinger')
        if (!res.ok) return
        const data = await res.json()
        const items = Array.isArray(data) ? data : (data.items || [])
        if (items.length > 0 && isMounted) {
          setSettings(items[0])
        }
      } catch (e) {
        // Fallback to hardcoded defaults
      }
    }
    loadSettings()
    return () => { isMounted = false }
  }, [])

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1200
      setIsMobile(mobile)
      if (!mobile) {
        setMobileMenuOpen(false)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Scroll listener to toggle transparent/solid header
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true)
      } else {
        setIsScrolled(false)
      }
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false)
    setDropdownOpen(false)
    setMobileDropdownOpen(false)
  }, [location])

  const dropdownServices = [
    { label: 'Tagarbejde', href: '/tagarbejde' },
    { label: 'Renovering af hus', href: '/renovering' },
    { label: 'Udskiftning af vinduer og døre', href: '/vinduer-og-doere' },
    { label: 'Træterasse', href: '/traeterasse' },
    { label: 'Gulve og lofte', href: '/gulve-og-lofter' },
    { label: 'Hegn og stakit', href: '/hegn-og-stakit' },
  ]

  const isServiceActive = dropdownServices.some(service => location.pathname === service.href)

  const handleMouseEnter = () => {
    if (!isMobile) setDropdownOpen(true)
  }

  const handleMouseLeave = () => {
    if (!isMobile) setDropdownOpen(false)
  }

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 ${
      isScrolled 
        ? 'bg-[#1e1e1e]/95 backdrop-blur-md border-b border-white/10 shadow-lg' 
        : 'bg-transparent border-b border-white/10'
    }`}>
      {/* Top Bar */}
      <div className={`transition-all duration-300 border-b border-white/5 ${
        isScrolled ? 'bg-[#1e1e1e]/40 py-1.5' : 'bg-[#1e1e1e]/20 py-2.5'
      } text-white px-4 sm:px-6 lg:px-8 text-sm`}>
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2">
          <div className="flex items-center gap-1.5 text-xs sm:text-sm">
            <span className="inline-block h-2 w-2 rounded-full bg-[#c08f66] animate-pulse" />
            <span className="font-medium text-stone-200">
              {settings?.topbjaelke_tekst?.da || settings?.topbjaelke_tekst || 'Din lokale tømrermester i Glyngøre & omegn'}
            </span>
          </div>
          <div className="flex items-center gap-6 text-xs sm:text-sm">
            <a href={`tel:${(settings?.telefon || '+45 20 53 27 67').replace(/\s+/g, '')}`} className="flex items-center gap-1.5 hover:text-[#c08f66] transition-colors whitespace-nowrap">
              <Phone className="h-3.5 w-3.5 text-[#c08f66]" />
              <span className="font-semibold text-stone-100">{settings?.telefon || '+45 20 53 27 67'}</span>
            </a>
            <a href={`mailto:${settings?.email || 'dvbolig@hotmail.com'}`} className="flex items-center gap-1.5 hover:text-[#c08f66] transition-colors whitespace-nowrap">
              <Mail className="h-3.5 w-3.5 text-[#c08f66]" />
              <span className="font-semibold text-stone-100">{settings?.email || 'dvbolig@hotmail.com'}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" aria-label="Gå til forsiden - Tømrermester Daniel Vandet">
            <img
              src={getOptimizedImageUrl(
                settings?.logo?.[0] || 'https://assets.auxodev.dk/assets/cf6a3489-9db0-408d-9584-8086b110e180/727ae610-4996-4e0c-beb1-82ff74ac0cde.png',
                { width: 300, quality: 90 }
              )}
              alt={settings?.firmanavn || "Tømrermester Daniel Vandet"}
              className="h-12 sm:h-14 w-auto object-contain"
              decoding="async"
            />
          </Link>

          {/* Desktop Menu (>= 1200px) */}
          {!isMobile && (
            <nav className="flex items-center gap-1" aria-label="Hovedmenu">
              {/* Forside */}
              <NavLink
                to="/"
                end
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-semibold rounded-sm transition-colors hover:text-[#c08f66] hover:bg-white/10 ${
                    isActive ? 'text-[#c08f66] bg-white/10' : 'text-white'
                  }`
                }
              >
                Forside
              </NavLink>

              {/* Vores kompetencer Dropdown */}
              <div
                ref={dropdownRef}
                className="relative"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className={`flex items-center gap-1 px-3 py-2 text-sm font-semibold rounded-sm transition-colors hover:text-[#c08f66] hover:bg-white/10 cursor-pointer ${
                    isServiceActive ? 'text-[#c08f66] bg-white/10' : 'text-white'
                  }`}
                >
                  <span>Vores kompetencer</span>
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown Menu */}
                {dropdownOpen && (
                  <div className="absolute left-0 mt-1 w-64 rounded-sm bg-[#1e1e1e] border border-white/10 shadow-xl py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
                    {dropdownServices.map((service) => (
                      <NavLink
                        key={service.href}
                        to={service.href}
                        className={({ isActive }) =>
                          `block px-4 py-2.5 text-sm font-semibold transition-colors hover:text-[#c08f66] hover:bg-white/5 ${
                            isActive ? 'text-[#c08f66] bg-white/5' : 'text-stone-200'
                          }`
                        }
                      >
                        {service.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>

              {/* Se vores arbejde */}
              <NavLink
                to="/referencer"
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-semibold rounded-sm transition-colors hover:text-[#c08f66] hover:bg-white/10 ${
                    isActive ? 'text-[#c08f66] bg-white/10' : 'text-white'
                  }`
                }
              >
                Se vores arbejde
              </NavLink>

              {/* Kontakt os */}
              <NavLink
                to="/kontakt"
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-semibold rounded-sm transition-colors hover:text-[#c08f66] hover:bg-white/10 ${
                    isActive ? 'text-[#c08f66] bg-white/10' : 'text-white'
                  }`
                }
              >
                Kontakt os
              </NavLink>
            </nav>
          )}

          {/* Mobile Menu Button (< 1200px) */}
          {isMobile && (
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="inline-flex items-center justify-center rounded-sm p-2 text-white hover:bg-white/10 hover:text-[#c08f66] focus:outline-none transition-colors cursor-pointer"
              aria-expanded={mobileMenuOpen}
              aria-label={mobileMenuOpen ? 'Luk hovedmenu' : 'Åbn hovedmenu'}
            >
              <span className="sr-only">{mobileMenuOpen ? 'Luk hovedmenu' : 'Åbn hovedmenu'}</span>
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          )}
        </div>
      </div>

      {/* Mobile Menu Panel (< 1200px) */}
      {isMobile && mobileMenuOpen && (
        <div className="border-t border-white/10 bg-[#1e1e1e] px-4 py-4 shadow-inner max-h-[calc(100vh-80px)] overflow-y-auto">
          <nav className="space-y-1" aria-label="Mobilmenu">
            {/* Forside */}
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `block px-3 py-3 text-base font-semibold rounded-sm transition-colors ${
                  isActive ? 'bg-white/5 text-[#c08f66]' : 'text-white hover:bg-white/5'
                }`
              }
            >
              Forside
            </NavLink>

            {/* Vores kompetencer Accordion */}
            <div>
              <button
                onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
                className={`flex w-full items-center justify-between px-3 py-3 text-base font-semibold rounded-sm transition-colors text-left cursor-pointer ${
                  isServiceActive ? 'bg-white/5 text-[#c08f66]' : 'text-white hover:bg-white/5'
                }`}
              >
                <span>Vores kompetencer</span>
                <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${mobileDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {mobileDropdownOpen && (
                <div className="mt-1 ml-4 border-l border-white/10 pl-2 space-y-1">
                  {dropdownServices.map((service) => (
                    <NavLink
                      key={service.href}
                      to={service.href}
                      className={({ isActive }) =>
                        `block px-3 py-2.5 text-sm font-semibold rounded-sm transition-colors ${
                          isActive ? 'text-[#c08f66] bg-white/5' : 'text-stone-300 hover:bg-white/5'
                        }`
                      }
                    >
                      {service.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>

            {/* Se vores arbejde */}
            <NavLink
              to="/referencer"
              className={({ isActive }) =>
                `block px-3 py-3 text-base font-semibold rounded-sm transition-colors ${
                  isActive ? 'bg-white/5 text-[#c08f66]' : 'text-white hover:bg-white/5'
                }`
              }
            >
              Se vores arbejde
            </NavLink>

            {/* Kontakt os */}
            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `block px-3 py-3 text-base font-semibold rounded-sm transition-colors ${
                  isActive ? 'bg-white/5 text-[#c08f66]' : 'text-white hover:bg-white/5'
                }`
              }
            >
              Kontakt os
            </NavLink>
          </nav>

          {/* Quick Contact Info in Mobile Menu */}
          <div className="mt-6 border-t border-white/10 pt-6 px-3 space-y-4">
            <div className="text-xs font-semibold text-stone-400 uppercase tracking-wider">
              Hurtig kontakt
            </div>
            <a href="tel:+4520532767" className="flex items-center gap-3 text-white hover:text-[#c08f66] transition-colors">
              <Phone className="h-5 w-5 text-[#c08f66]" />
              <span className="font-semibold whitespace-nowrap">+45 20 53 27 67</span>
            </a>
            <a href="mailto:dvbolig@hotmail.com" className="flex items-center gap-3 text-white hover:text-[#c08f66] transition-colors">
              <Mail className="h-5 w-5 text-[#c08f66]" />
              <span className="font-semibold whitespace-nowrap">dvbolig@hotmail.com</span>
            </a>
          </div>
        </div>
      )}
    </header>
  )
}
